import {
  EOA_PREFERENCE_TTL_MS,
  SELECTED_EOA_LOCAL_STORAGE_KEY,
  SELECTED_EOA_PREFERENCE_LOCAL_STORAGE_KEY,
} from '../constants';
import { EOAListItem } from '../interfaces/UserDetailsInterface';

interface SelectedEoaPreference {
  eoaId: string;
  selectedAt: number;
}

const parsePreference = (raw: string | null): SelectedEoaPreference | null => {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as SelectedEoaPreference;
    if (typeof parsed.eoaId === 'string' && typeof parsed.selectedAt === 'number') {
      return parsed;
    }
  } catch {
    return null;
  }

  return null;
};

const isEoaPreferenceExpired = (preference: SelectedEoaPreference, now = Date.now()): boolean =>
  now - preference.selectedAt >= EOA_PREFERENCE_TTL_MS;

const getStoredPreference = (): SelectedEoaPreference | null =>
  parsePreference(localStorage.getItem(SELECTED_EOA_PREFERENCE_LOCAL_STORAGE_KEY));

const migrateLegacyPreference = (legacyEoaId: string): SelectedEoaPreference => {
  const preference = { eoaId: legacyEoaId, selectedAt: Date.now() };
  localStorage.setItem(SELECTED_EOA_PREFERENCE_LOCAL_STORAGE_KEY, JSON.stringify(preference));
  return preference;
};

const getValidPersistedEoaId = (availableEoas: EOAListItem[], now = Date.now()): string | null => {
  const availableEoaIds = new Set(availableEoas.map((eoa) => eoa.id));
  let preference = getStoredPreference();

  if (!preference) {
    const legacyEoaId = localStorage.getItem(SELECTED_EOA_LOCAL_STORAGE_KEY);
    if (legacyEoaId && availableEoaIds.has(legacyEoaId)) {
      preference = migrateLegacyPreference(legacyEoaId);
    }
  }

  if (!preference || !availableEoaIds.has(preference.eoaId) || isEoaPreferenceExpired(preference, now)) {
    return null;
  }

  return preference.eoaId;
};

const persistEoaSelection = (eoaId: string, now = Date.now()): void => {
  const preference: SelectedEoaPreference = { eoaId, selectedAt: now };
  localStorage.setItem(SELECTED_EOA_PREFERENCE_LOCAL_STORAGE_KEY, JSON.stringify(preference));
  localStorage.setItem(SELECTED_EOA_LOCAL_STORAGE_KEY, eoaId);
};

export type { SelectedEoaPreference };
export { isEoaPreferenceExpired, getValidPersistedEoaId, persistEoaSelection, migrateLegacyPreference };
